import { cn } from "@/lib/utils";

// One MUI `SvgIcon` body, shared by every ported glyph.
//
// MUI renders all of them the same way — a `viewBox="0 0 24 24"` svg holding a
// single `fill: currentColor` path, `aria-hidden` unless given a `titleAccess`
// (`SvgIcon.js:44-56,78`). Only the box size differs between call sites, so
// that is the one thing this takes as an argument.
//
// It exists because the same body had been written out three times — once for
// the arrows, once for the alert glyphs, once for the caret — and two of those
// were byte-identical apart from a size class.
//
// **The caret is deliberately not one of these.** `MuiCaretGlyph`
// (`mui-select-caret.tsx`) carries an `open` state, its own ink and a rotation,
// so folding it in would mean growing this factory three parameters to absorb a
// single caller. It keeps its own body and cites the same `SvgIcon` rules.

interface MuiGlyphProps {
  className?: string;
}

/**
 * Builds one MUI glyph component.
 *
 * @param slot  the `data-slot` the component stamps, for test and style hooks.
 * @param d     the path, copied byte-for-byte from the pinned MUI source.
 * @param size  the box, as Tailwind classes — MUI's `fontSize` in disguise.
 */
export function muiGlyph(slot: string, d: string, size: string) {
  return function MuiGlyph({ className }: MuiGlyphProps) {
    return (
      <svg
        data-slot={slot}
        // Decoration at every call site so far: each sits inside a control or
        // an `aria-hidden` slot that already carries the accessible name.
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        // MUI's own `width: 1em; height: 1em` (`SvgIcon.js:44-45`), as
        // presentation attributes: a fallback only, since the `size` class is
        // an author rule and outranks them wherever the stylesheet loads.
        // Without them a glyph rendered with no stylesheet — a browser test
        // that deliberately skips `index.css` — takes the UA's 300x150 box
        // and shoves the page around it, which is how a search box's
        // magnifier pushed the catalog's static batch toolbar out of view.
        width="1em"
        height="1em"
        className={cn(size, "shrink-0", className)}
      >
        <path d={d} fill="currentColor" />
      </svg>
    );
  };
}
